import React, { useState } from "react";
import { Phone, Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { BRAND } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function Contact() {
  useSeo("contact");
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Inquiry sent! Our team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", project_interest: "", message: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div>
      <section className="bg-navy py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-teal mb-3">Get in Touch</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white">Contact Us</h1>
          <p className="mt-4 text-white/70 max-w-xl">Share your requirement or reach us instantly on WhatsApp.</p>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 grid gap-12 lg:grid-cols-2">
          <Reveal>
            <form onSubmit={submit} className="rounded-2xl bg-white p-8 ring-1 ring-slate-100 shadow-sm space-y-4" data-testid="contact-form">
              <h2 className="font-display text-2xl font-semibold text-slate-900">Send an Inquiry</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="contact-name-input" />
                <Input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="contact-email-input" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" />
                <Input placeholder="Project of interest" value={form.project_interest} onChange={set("project_interest")} data-testid="contact-project-input" />
              </div>
              <Textarea required rows={5} placeholder="Your message" value={form.message} onChange={set("message")} data-testid="contact-message-input" />
              <button type="submit" disabled={loading} data-testid="contact-submit-button"
                className="inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3 font-semibold text-white hover:bg-navy-dark transition-colors disabled:opacity-60">
                {loading ? "Sending..." : <>Send Inquiry <Send className="h-4 w-4" /></>}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="space-y-6">
            <div className="rounded-2xl bg-slate-50 p-8 space-y-5">
              {[
                { i: MapPin, t: "Office", v: BRAND.address },
                { i: Phone, t: "Phone", v: BRAND.phone, href: `tel:${BRAND.phone}` },
                { i: Mail, t: "Email", v: BRAND.email, href: `mailto:${BRAND.email}` },
              ].map((c, k) => (
                <div key={k} className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal/10 text-teal"><c.i className="h-5 w-5" /></span>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">{c.t}</div>
                    {c.href ? <a href={c.href} className="text-slate-800 font-medium hover:text-teal">{c.v}</a> : <div className="text-slate-800 font-medium">{c.v}</div>}
                  </div>
                </div>
              ))}
            </div>
            <a href={BRAND.whatsapp} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-button"
              className="flex items-center justify-center gap-2 rounded-2xl bg-teal p-5 font-semibold text-white hover:bg-teal-dark transition-colors">
              <MessageCircle className="h-5 w-5" /> Chat on WhatsApp
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
